import {Elements} from "@stripe/react-stripe-js";
import {stripeIsConfigured, stripePromise} from "../../../App";
import {useEffect, useState} from "react";
import {useProposal} from "../../../hooks/useProposal";
import {Outlet, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";

const getPaymentIntent = (response) =>
  response?.paymentIntent || response?.data?.paymentIntent || response?.data;

const PaymentPage = () => {
  const {id: paymentReference} = useParams();
  const [clientSecret, setClientSecret] = useState("");
  const [amount, setAmount] = useState(null);
  const [paymentError, setPaymentError] = useState("");
  const [isPreparing, setIsPreparing] = useState(true);
  const {handlePayProposal} = useProposal();
  const {t} = useTranslation();

  useEffect(() => {
    let isActive = true;

    const preparePayment = async () => {
      setIsPreparing(true);
      setPaymentError("");
      try {
        if (!stripeIsConfigured) {
          throw new Error("Le paiement est temporairement indisponible.");
        }
        if (!paymentReference) {
          const secret = new URLSearchParams(window.location.search).get(
            "payment_intent_client_secret",
          );
          if (!secret) throw new Error("Le paiement est introuvable.");
          if (isActive) setClientSecret(secret);
          return;
        }

        if (!/^[a-f0-9]{64}$/.test(paymentReference)) {
          throw new Error("Ce lien de paiement est invalide ou a expiré.");
        }
        const response = await handlePayProposal({paymentReference});
        const paymentIntent = getPaymentIntent(response);
        const secret =
          paymentIntent?.client_secret ||
          paymentIntent?.clientSecret ||
          response?.clientSecret;

        if (!secret) {
          throw new Error(
            "Le serveur n'a pas renvoyé le secret nécessaire au formulaire Stripe.",
          );
        }

        if (isActive) {
          setClientSecret(secret);
          setAmount(paymentIntent?.amount ?? response?.amount ?? null);
        }
      } catch (error) {
        if (isActive) {
          setClientSecret("");
          setPaymentError(
            error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message ||
              "Impossible de préparer le paiement. Veuillez réessayer.",
          );
        }
      } finally {
        if (isActive) setIsPreparing(false);
      }
    };

    preparePayment();
    return () => {
      isActive = false;
    };
  }, [paymentReference]);

  const options = {
    clientSecret,
    appearance: {theme: "stripe"},
    loader: "auto",
  };

  return (
    <div className="w-full min-h-full h-fit flex flex-col justify-center items-center">
      {amount !== null && (
        <div className="text-lg text-center font-semibold mb-4 bg-[#ff6600] text-white w-5/6 md:w-3/4 xl:w-1/2 p-2 !rounded-md">
          {t("AmountToPay")}: €{(Number(amount) / 100).toFixed(2)}
        </div>
      )}

      {isPreparing && (
        <div className="w-5/6 md:w-3/4 xl:w-1/2 text-center">
          Préparation du paiement...
        </div>
      )}

      {paymentError && (
        <div className="w-5/6 md:w-3/4 xl:w-1/2 text-center">
          <div role="alert" className="rounded-md bg-red-50 p-3 text-red-700">
            {paymentError}
          </div>
          <a href="/messages" className="btn mt-3 bg-[#1A64B9] text-white">
            Retour à la messagerie
          </a>
        </div>
      )}

      {clientSecret && (
        <Elements key={clientSecret} options={options} stripe={stripePromise}>
          <Outlet />
        </Elements>
      )}
    </div>
  );
};

export default PaymentPage;
